import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SendDeliveryDialog from "../components/program/SendDeliveryDialog";
import ClientFileChecklist from "../components/program/ClientFileChecklist";
import {
  ArrowLeft, Copy, ExternalLink, RefreshCw, Rocket, Plus, Trash2,
  CheckCircle2, Clock, Loader2, ClipboardCheck, Link2, AlertCircle,
  Eye, MessageSquare, Send, PauseCircle, Check, Video, FolderOpen,
  Users, Search, EyeOff, FileText, CalendarPlus, BellRing,
} from "lucide-react";

const ROUND_STATUSES = [
  { value: "pending", label: "Not sent" },
  { value: "delivered", label: "Sent — with client" },
  { value: "approved", label: "Approved" },
];

/* ── A round's links ─────────────────────────────────────────────────────────
 *
 * What the client opens for this round — one row per deliverable. Editing here
 * saves without emailing anyone (fix a wrong link, add the 360s that came in
 * late); "Send to client" is the button that notifies.
 */
function RoundLinks({ round, kinds, busy, onSave }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const links = round.links || [];

  const start = () => {
    setRows(links.length
      ? links.map((l) => ({ url: l.url, kind: l.kind || "", label: l.label || "" }))
      : [{ url: "", kind: "", label: "" }]);
    setEditing(true);
  };
  const update = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const ok = await onSave(rows
      .map((r) => ({ url: r.url.trim(), kind: r.kind || null, label: r.label.trim() || null }))
      .filter((r) => r.url || r.label));
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {links.length === 0 && <span className="text-xs text-muted-foreground">No links yet.</span>}
        {links.map((l) => (
          <a key={l.id || l.url} href={l.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline max-w-full">
            <Link2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{l.display_label}</span>
          </a>
        ))}
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={start} disabled={busy}>
          {links.length ? "Edit links" : "Add links"}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border p-2">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2">
          <select value={r.kind} onChange={(e) => update(i, { kind: e.target.value })}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm shrink-0"
            aria-label="What this link is">
            <option value="">Type…</option>
            {kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          <Input value={r.label} onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Description (optional)" className="sm:w-44" maxLength={80} />
          <Input value={r.url} onChange={(e) => update(i, { url: e.target.value })} placeholder="https://…" />
          <Button size="icon" variant="ghost" className="shrink-0" aria-label="Remove this link"
            onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs"
          onClick={() => setRows((rs) => [...rs, { url: "", kind: "", label: "" }])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add another link
        </Button>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          <Button size="sm" className="rounded-full" onClick={save} disabled={busy}>
            Save links
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Saving updates their dashboard without emailing anyone.</p>
    </div>
  );
}

/* ── The page's three tabs ───────────────────────────────────────────────────
 *
 * This page used to be eleven cards in one column with a scrollspy rail beside
 * it to find your way around. The rail worked, and it was the wrong fix: a page
 * that needs its own table of contents is a page doing too many jobs.
 *
 * The three tabs are the three things a PM actually comes here to do, and the
 * order is the order a project moves through them. Collections and the pause
 * left entirely — finance-only, and it now lives on the Payments tab next to
 * the money it is about.
 *
 * `badge` is the part doing the real work against "it's overwhelming": the tab
 * strip says which tab wants something from you. Deliberately sparse — a count
 * on every tab is wallpaper, and then the one that matters reads as decoration.
 */
const TABS = [
  {
    id: "collect",
    label: "Collect",
    blurb: "Get their files in, then start the clock.",
    // Files all in and nobody has started it — the one moment this is the most
    // important thing on the page. Otherwise: what's still outstanding.
    badge: (d) => {
      if (!d.timeline_started && d.files?.files_complete) return { dot: true, tone: "go" };
      const n = d.files?.outstanding_required || 0;
      return n ? { count: n, tone: "warn" } : null;
    },
  },
  {
    id: "deliver",
    label: "Deliver",
    blurb: "Send each delivery — and see what actually went.",
    badge: (d) => {
      const n = (d.payments?.milestones || []).filter(
        (m) => m.state !== "paid" && m.state !== "waived" && !m.forecast,
      ).length;
      return n ? { count: n, tone: "warn" } : null;
    },
  },
  {
    id: "client",
    label: "Client view",
    blurb: "What they're reading, and everything shared with them.",
  },
];

const BADGE_TONE = {
  warn: "bg-amber-100 text-amber-800 border-amber-200",
  risk: "bg-red-100 text-red-800 border-red-200",
  go: "bg-green-100 text-green-800 border-green-200",
};
const DOT_TONE = { warn: "bg-amber-500", risk: "bg-red-500", go: "bg-green-500" };

export default function ProjectTrackerManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Why the first load failed, if it did. A 403 or 404 used to toast and then
  // render nothing at all — a black page with no way back.
  const [loadError, setLoadError] = useState(null);
  const loadedRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [busyRound, setBusyRound] = useState(null);
  const [posting, setPosting] = useState(false);
  const [updateBody, setUpdateBody] = useState("");
  const [tab, setTab] = useState("collect");
  usePageWantsSticky();

  // editable form state
  const [clientEmail, setClientEmail] = useState("");
  const [intro, setIntro] = useState("");
  const [wMin, setWMin] = useState("");
  const [wMax, setWMax] = useState("");
  const [links, setLinks] = useState([]);

  const hydrate = (d) => {
    loadedRef.current = true;
    setData(d);
    setClientEmail(d.client_email || "");
    setIntro(d.intro_message || "");
    setWMin(d.eta_weeks_min ?? "");
    setWMax(d.eta_weeks_max ?? "");
    setLinks(d.quick_links || []);
  };

  const fetchTracker = useCallback(async () => {
    try {
      const res = await apiClient.get(`/project-tracker/program/${id}`);
      hydrate(res.data);
      setLoadError(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Before the page exists, the error IS the page — say it there rather
      // than in a toast over a blank screen. Once loaded, a failed refresh
      // keeps what's on screen and just says so.
      if (loadedRef.current) toast.error(detail || "Couldn't refresh the tracker.");
      else setLoadError({ status: e.response?.status, detail: typeof detail === "string" ? detail : null });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTracker(); }, [fetchTracker]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        client_email: clientEmail || null,
        intro_message: intro || null,
        eta_weeks_min: wMin === "" ? null : Number(wMin),
        eta_weeks_max: wMax === "" ? null : Number(wMax),
        quick_links: links.filter((l) => l.label && l.url),
      };
      const res = await apiClient.patch(`/project-tracker/program/${id}`, payload);
      hydrate(res.data);
      toast.success("Tracker saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const startClock = async () => {
    setStarting(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/start-clock`, {
        eta_weeks_min: wMin === "" ? null : Number(wMin),
        eta_weeks_max: wMax === "" ? null : Number(wMax),
      });
      toast.success(res.data.email_sent ? "Clock started — client emailed!" : "Clock started");
      if (!res.data.email_sent) toast.message("No client email set, so no email was sent.");
      await fetchTracker();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the clock");
    } finally {
      setStarting(false);
    }
  };

  const [sendRound, setSendRound] = useState(null);
  const [sendingWelcome, setSendingWelcome] = useState(false);

  const sendWelcome = async () => {
    setSendingWelcome(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/send-welcome`);
      hydrate(res.data);
      // Reported from what the send actually did, not from a mode read off the
      // page: the welcome email is on the Getting-started switch while the rest
      // of this page is on the delivery one, and guessing gets that backwards.
      const send = res.data.send || {};
      if (send.sent && send.status === "redirected") {
        toast.success(`Welcome email redirected to the test address (${send.to}).`);
      } else if (send.sent) {
        toast.success("Welcome email sent.");
      } else if (send.status === "no_recipient") {
        toast.warning("Nothing left the building — there's no client email on this tracker.");
      } else if (send.status === "failed") {
        toast.error(`Couldn't send: ${send.error || "the mail service rejected it"}.`);
      } else {
        toast.warning(
          `Nothing left the building — welcome emails are ${
            res.data.email_modes?.onboarding || "paused"}.`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send the welcome email.");
    } finally {
      setSendingWelcome(false);
    }
  };

  const sendRoundToClient = async ({ links: roundLinks, note, recipients, to, cc, save_to_team }) => {
    const key = sendRound.key;
    try {
      const res = await apiClient.post(
        `/project-tracker/program/${id}/rounds/${key}/send`,
        { links: roundLinks, note, recipients, to, cc, save_to_team },
      );
      hydrate(res.data);
      setSendRound(null);
      const s = res.data.send || {};
      if (s.sent > 0) {
        // People, not emails — one email with two CCs reached three.
        const n = s.reached ?? s.sent;
        toast.success(`${sendRound.label} sent to ${n} recipient${n === 1 ? "" : "s"}.`);
      } else if (s.reason) {
        toast.warning(`Link saved, but nothing was emailed — ${s.reason}.`);
      } else {
        // Paused/test mode, or a delivery failure. The round is still marked
        // delivered because the PM did press send; saying "sent" would not be true.
        toast.warning("Link saved and the round marked delivered, but no email left — check the send mode in Tracker admin.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send that delivery.");
    }
  };

  const setRound = async (roundKey, patch) => {
    setBusyRound(roundKey);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/rounds/${roundKey}`, patch);
      hydrate(res.data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update that round");
      return false;
    } finally {
      setBusyRound(null);
    }
  };

  const postUpdate = async () => {
    const body = updateBody.trim();
    if (!body) return;
    setPosting(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/updates`, { body });
      hydrate(res.data);
      setUpdateBody("");
      toast.success("Update posted to the client's page");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post that update");
    } finally {
      setPosting(false);
    }
  };

  const deleteUpdate = async (updateId) => {
    try {
      const res = await apiClient.delete(`/project-tracker/program/${id}/updates/${updateId}`);
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't remove that update");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Rotate the link? The old link will stop working.")) return;
    try {
      await apiClient.post(`/project-tracker/program/${id}/regenerate-token`);
      await fetchTracker();
      toast.success("New link generated");
    } catch (e) {
      toast.error("Couldn't rotate the link");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(data.public_url);
    toast.success("Link copied");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (!data) {
    return <TrackerLoadError programId={id} error={loadError} onRetry={() => { setLoading(true); fetchTracker(); }} />;
  }

  const files = data.files || {};
  const started = data.timeline_started;
  // Sales (and anyone else the server lets read but not change) gets the same
  // page with its controls switched off. `can_edit` comes from the server, the
  // same gate every mutation checks; an older backend that doesn't send it is
  // treated as editable, which is what this page always assumed.
  const readOnly = data.can_edit === false;

  return (
    <div className="max-w-5xl mx-auto px-4 pb-6">
      <TrackerTabs
        data={data}
        tab={tab}
        setTab={setTab}
        onBack={() => navigate(`/programs/${id}`)}
        subtitle={`${data.program_name}${data.client_name ? ` · ${data.client_name}` : ""}`}
      >

      {readOnly && (
        <div className="rounded-xl border bg-muted/40 p-3 flex items-start gap-2.5 text-sm" data-testid="tracker-read-only">
          <Eye className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            <strong className="text-foreground">View only.</strong> You can see everything the team and the
            client see here; the project's PM makes the changes. Use the client link to open their page.
          </p>
        </div>
      )}

      {/* ── COLLECT ──────────────────────────────────────────────────────── */}
      {tab === "collect" && (<>
      {/* Shareable link */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Link2 className="w-4 h-4" /> Client link</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            One link for the client's whole project. Before the clock starts it shows their file checklist; after, it shows live progress. Safe to forward to their vendors.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={data.public_url} className="font-mono text-xs" />
            <Button variant="outline" size="sm" onClick={copyLink} className="shrink-0"><Copy className="w-4 h-4" /></Button>
            <a href={data.public_url} target="_blank" rel="noreferrer" className="shrink-0">
              <Button variant="outline" size="sm"><ExternalLink className="w-4 h-4" /></Button>
            </a>
            {!readOnly && (
              <Button variant="ghost" size="sm" onClick={regenerate} className="shrink-0" title="Rotate link"><RefreshCw className="w-4 h-4" /></Button>
            )}
          </div>

          {/* Handing the client the link is the moment they need telling what to
              do with it. Nothing used to reach them until "your clock started",
              which fires after every file is already in. */}
          {!readOnly && (<>
          <div className="rounded-xl border p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Welcome email</p>
              <p className="text-xs text-muted-foreground">
                {data.welcome_sent_at
                  ? `Sent ${new Date(data.welcome_sent_at).toLocaleDateString()} to ${data.client_email}. `
                  : ""}
                Their to-do list, their documents, and the link — sent to {data.client_email || "the client"}.
              </p>
            </div>
            <Button size="sm" variant={data.welcome_sent_at ? "outline" : "default"}
              className="rounded-full shrink-0" onClick={sendWelcome}
              disabled={sendingWelcome || !data.client_email}>
              {sendingWelcome ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              {data.welcome_sent_at ? "Send again" : "Send welcome email"}
            </Button>
          </div>
          {!data.client_email && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> Set the client email in Tracker settings to enable this.
            </p>
          )}
          </>)}
        </CardContent>
      </Card>


      {/* The list itself, on the page whose job is chasing it. It used to be
          one line and a link out to VEXOS Setup — so the moment a PM's job was
          files, the page built for files sent them somewhere else. */}
      <ClientFileChecklist programId={id} data={data} onChanged={fetchTracker} readOnly={readOnly} />

      {/* Start the clock */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Rocket className="w-4 h-4" /> Start the clock</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {started ? (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm text-green-900">
                <p className="font-medium">Clock started.</p>
                <p className="text-green-800 mt-0.5">
                  Started by {data.timeline_started_by_name || data.timeline_started_by || "a PM"}.
                  Current phase: <strong>{data.stage}</strong>.
                  {data.eta_weeks_text && <> Client sees: {data.eta_weeks_text}{data.eta_target_text ? ` · target ${data.eta_target_text}` : ""}.</>}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className={`rounded-xl border p-4 flex gap-3 ${files.files_complete ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
                {files.files_complete
                  ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  : <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                <div className="text-sm">
                  {!files.has_checklist ? (
                    <p className="text-amber-900">
                      No file list exists on this project yet — create one above, and the clock
                      gate starts measuring against it.
                    </p>
                  ) : files.files_complete ? (
                    <p className="text-green-900">All required files are in ({files.required_complete}/{files.required_total}). You're clear to start.</p>
                  ) : (
                    <p className="text-amber-900">
                      Waiting on required files — <strong>{files.required_complete}/{files.required_total}</strong> collected
                      ({files.completion_percentage}% overall). The button unlocks at 100% of required items.
                    </p>
                  )}
                </div>
              </div>
              {!clientEmail && !readOnly && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="w-3.5 h-3.5" /> Set the client email below so they get the "you've started" notification.
                </div>
              )}
              {!readOnly && (
                <Button onClick={startClock} disabled={!data.can_start_clock || starting} className="rounded-full">
                  {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                  Start the clock
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      </>)}

      {/* ── DELIVER ──────────────────────────────────────────────────────── */}
      {tab === "deliver" && (<ReadOnlyScope readOnly={readOnly}>
      {/* Delivery rounds */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Delivery rounds ({(data.rounds || []).map((r) => r.label).join(" → ")})</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Each round shows on the client's page with the date you sent it. A round can carry several links — orbit
            and keyframes, stills, 360s, one per unit — each with its own label.
          </p>
          <p className="text-xs text-muted-foreground -mt-2">
            <strong className="text-foreground">Two rounds of revision on color</strong>:
            {(data.rounds || []).some((r) => r.key === "clay")
              ? " the clay notes become V1 (colorized + materialized), the client's V1 notes become V2, and their V2 notes become the final files."
              : " V1 is the first look, the client's V1 notes become V2, and their V2 notes become the final files. This project was past V1 before the clay step existed, so it has none."}
            {" "}The client's page states two — don't promise a third. These are deliveries, not payments: the
            payment schedule has its own gates.
          </p>
          {(data.rounds || []).map((r) => (
            <div key={r.key} className="rounded-xl border p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">
                    <span className="font-mono text-xs text-muted-foreground mr-1.5">{r.label}</span>{r.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Client sees: {r.status_text}
                    {r.delivered_text ? ` · sent ${r.delivered_text}` : ""}
                    {r.approved_text ? ` · approved ${r.approved_text}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {busyRound === r.key && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  <Button size="sm" variant={r.status === "pending" ? "default" : "outline"}
                    className="rounded-full text-xs h-8"
                    onClick={() => setSendRound(r)}>
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {r.delivered_text ? "Resend" : "Send to client"}
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {ROUND_STATUSES.map((s) => (
                  <Button key={s.value} size="sm" variant={r.status === s.value ? "default" : "outline"}
                    className="rounded-full text-xs h-7"
                    disabled={busyRound === r.key}
                    onClick={() => setRound(r.key, { status: s.value })}>
                    {s.label}
                  </Button>
                ))}
              </div>
              <RoundLinks
                round={r}
                kinds={data.round_link_kinds || []}
                busy={busyRound === r.key}
                onSave={(roundLinks) => setRound(r.key, { links: roundLinks })}
              />
              <Input defaultValue={r.note || ""} placeholder="Short note for the client (optional)"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.note || "")) setRound(r.key, { note: v }); }} />
            </div>
          ))}
        </CardContent>
      </Card>

      {sendRound && (
        <SendDeliveryDialog
          open
          onClose={() => setSendRound(null)}
          onSend={sendRoundToClient}
          roundLabel={sendRound.label}
          roundTitle={sendRound.title}
          linkLabel="Review Studio link"
          linkHint="The client opens these from their dashboard, so their notes land against the right round."
          multiLink
          linkKinds={data.round_link_kinds || []}
          teamRoles={data.team_roles || []}
          initialLinks={sendRound.links || []}
          initialNote={sendRound.note || ""}
          recipients={data.delivery_recipients || []}
          onRecipientsChange={(list) => setData((d) => ({ ...d, delivery_recipients: list }))}
          emailMode={data.email_mode}
          alreadySentText={sendRound.delivered_text}
          programId={id}
        />
      )}


      {/* Andre drives the VEXOS rail from the project's own VEXOS tab, so it
          is read-only here — but the client sees both rails side by side, and
          "did they get V1?" should not take two pages to answer. */}
      <VexosRailCard data={data} programId={id} />

      {/* Proof of what actually left the building. The backend has always
          returned this log; nothing rendered it. */}
      <SendHistoryCard data={data} />

      {/* No payment-schedule widget here on purpose. A PM's job on this page is
          dates and deliverables; the money lives in the Payments tab, where the
          finance gate already is. The client always sees their own schedule. */}

      {/* When each milestone lands — the PM's call, shown to the client */}
      <div>
        <ForecastCard id={id} data={data} hydrate={hydrate} />
      </div>

      {/* The client's three-week heads-up on the final payment — approved here */}
      <FinalPaymentNoticeCard id={id} data={data} hydrate={hydrate} readOnly={readOnly} />

      {/* Client updates feed */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Updates for the client</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Plain-English notes that appear on the client's tracker, newest first. No email is sent — it's there when they look.
          </p>
          {!readOnly && <div className="flex items-start gap-2">
            <textarea value={updateBody} onChange={(e) => setUpdateBody(e.target.value)} rows={2}
              className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g. Your V1 exteriors are rendering now — you'll have them Thursday." />
            <Button onClick={postUpdate} disabled={posting || !updateBody.trim()} className="rounded-full shrink-0">
              {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>}
          <div className="space-y-2">
            {(data.updates || []).length === 0 && <p className="text-xs text-muted-foreground">No updates posted yet.</p>}
            {(data.updates || []).map((u) => (
              <div key={u.id} className="rounded-xl border p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{u.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">{u.created_text}{u.author_name ? ` · ${u.author_name}` : ""}</p>
                </div>
                {!readOnly && (
                  <Button variant="ghost" size="sm" onClick={() => deleteUpdate(u.id)} className="shrink-0">
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      </ReadOnlyScope>)}

      {/* ── CLIENT VIEW ──────────────────────────────────────────────────── */}
      {tab === "client" && (<ReadOnlyScope readOnly={readOnly}>
      {/* What the client sees right now */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Eye className="w-4 h-4" /> What the client sees right now</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {(data.phases || []).map((p, i) => (
              <span key={p.key} className="flex items-center gap-1.5">
                {i > 0 && <span className="text-muted-foreground/40 text-xs">→</span>}
                <span className={
                  p.state === "done" ? "text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-700"
                    : p.state === "current" ? "text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-600 text-white"
                    : p.state === "paused" ? "text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500 text-white"
                    : "text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground"
                }>
                  {p.state === "done" && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                  {p.label}
                </span>
              </span>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            The client's phase rail mirrors this program's HQ stage — <strong className="text-foreground">{data.stage}</strong> — automatically.
            Move the project on the Programs board and their page follows. {data.progress_percent != null && <>They see <strong className="text-foreground">{data.progress_percent}%</strong> overall progress.</>}
          </p>
          {data.on_hold && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
              <PauseCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                This project is <strong>On Hold</strong>, so the client sees a paused banner
                {data.paused_at_phase ? <> at <strong>{data.paused_at_phase}</strong></> : null}. Consider posting an update below
                explaining what will restart it.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Anchors live on wrappers for these, not on the cards: each component
          already takes `id` as the PROGRAM id, so reusing the prop would swap
          a section anchor for a program lookup. */}
      {/* Meetings on the client dashboard */}
      <div>
        <MeetingsCard id={id} data={data} hydrate={hydrate} />
      </div>

      {/* Documents on the client dashboard */}
      <div>
        <DocumentsCard id={id} data={data} hydrate={hydrate} />
      </div>

      {/* Who the client can reach on OUR side */}
      <div>
        <ContactCardsCard id={id} />
      </div>

      {/* Who the client has added */}
      <div>
        <TeamCard data={data} onChanged={fetchTracker} />
      </div>

      {/* Settings */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Tracker settings</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="text-sm">Client email (notified when the clock starts)</Label>
            <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@company.com" className="mt-1" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Estimated weeks — min</Label>
              <Input type="number" min="0" value={wMin} onChange={(e) => setWMin(e.target.value)} placeholder="3" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm">Estimated weeks — max</Label>
              <Input type="number" min="0" value={wMax} onChange={(e) => setWMax(e.target.value)} placeholder="4" className="mt-1" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">The client sees a week range (e.g. "3–4 weeks") with a ±{data.eta_variance_days || 4} day variance — never a hard date.</p>

          <div>
            <Label className="text-sm">Welcome note (optional, shown at the top of the client page)</Label>
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={2}
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="A short friendly note to your client…" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Quick links</Label>
              <Button variant="ghost" size="sm" onClick={() => setLinks([...links, { label: "", url: "" }])}>
                <Plus className="w-4 h-4 mr-1" /> Add link
              </Button>
            </div>
            <div className="space-y-2">
              {links.length === 0 && <p className="text-xs text-muted-foreground">Add shared drives, kickoff notes, contracts, etc.</p>}
              {links.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={l.label} placeholder="Label" className="w-1/3"
                    onChange={(e) => setLinks(links.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                  <Input value={l.url} placeholder="https://…" className="flex-1"
                    onChange={(e) => setLinks(links.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} />
                  <Button variant="ghost" size="sm" onClick={() => setLinks(links.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {!readOnly && (
            <Button onClick={save} disabled={saving} className="rounded-full">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save settings
            </Button>
          )}
        </CardContent>
      </Card>
      </ReadOnlyScope>)}
      </TrackerTabs>
    </div>
  );
}

/* ── Section navigation: the sticky rail and its scrollspy ─────────────────── */
/*
 * Eleven cards in one column is a long scroll with no sense of place. This adds
 * the two things that fixes: jump anywhere in one click, and always know where
 * you are.
 *
 * Layout is content-first — the rail sits to the RIGHT. The app already owns
 * the left edge with its own dark sidebar, and a second left-hand nav two
 * inches from the first reads as a bug.
 */

/*
 * Claim sticky positioning for this page while it's mounted, and hand it back
 * on the way out.
 *
 * HQ sets `overflow-x: hidden` on html/body so a stray wide table can't shove
 * the fixed sidebar off a phone screen. The side effect is that BODY becomes a
 * scroll container, which silently kills `position: sticky` against the
 * viewport — the element renders, takes the class, reports `position: sticky`,
 * and scrolls away anyway. Nothing errors; it just doesn't stick, which is why
 * this is worth a named hook instead of a line of CSS someone deletes later.
 * `overflow-x: clip` clips identically without creating the scroll container.
 * Same trick the client portal uses (see index.css).
 */
/* ── Read-only and load-failure states ──────────────────────────────────────
 *
 * `ReadOnlyScope` is a <fieldset disabled>: the browser itself switches off
 * every button, input and textarea inside, so a view-only reader cannot fire a
 * request the server will refuse — and no card has to learn a new prop to get
 * there. Plain links (review links, the VEXOS-tab link) still work. With
 * `readOnly` false it is a fieldset that does nothing.
 */
function ReadOnlyScope({ readOnly, children }) {
  return (
    <fieldset disabled={readOnly} className="min-w-0 border-0 p-0 m-0 space-y-6">
      {children}
    </fieldset>
  );
}

function TrackerLoadError({ programId, error, onRetry }) {
  const status = error?.status;
  const title = status === 403 ? "You don't have access to this tracker"
    : status === 404 ? "This project couldn't be found"
    : "The tracker wouldn't load";
  const body = status === 403
    ? "Client Trackers are open to PMs, sales, finance and admins. If you need this one, ask the project's PM or an admin."
    : status === 404
      ? "It may have been deleted, or the link is out of date."
      : "Nothing has changed on the project. Try again, and tell an engineer if it keeps happening.";
  return (
    <div className="max-w-xl mx-auto px-4 py-16" data-testid="tracker-load-error">
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <AlertCircle className="w-8 h-8 mx-auto text-amber-500" />
          <p className="text-base font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{body}</p>
          {error?.detail && <p className="text-xs text-muted-foreground">{error.detail}</p>}
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Link to={`/programs/${programId}`}>
              <Button variant="outline" className="rounded-full">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to the project
              </Button>
            </Link>
            {status !== 403 && status !== 404 && (
              <Button className="rounded-full" onClick={onRetry}>
                <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function usePageWantsSticky() {
  useEffect(() => {
    document.documentElement.classList.add("sticky-page");
    return () => document.documentElement.classList.remove("sticky-page");
  }, []);
}

/* ── The tab strip ──────────────────────────────────────────────────────────
 *
 * What replaced ~200 lines of scrollspy: a measured sticky header, three tabs,
 * and no rail. The scrollspy was well-behaved — it handled the throttled-tab
 * case, the tall-section case, the smooth-scroll-not-honoured case — and none
 * of that work is needed once the page stops being one eleven-card scroll.
 *
 * The one thing kept from it: HQ sets `overflow-x: hidden` on html/body, which
 * makes BODY a scroll container and silently kills `position: sticky` against
 * the viewport. The element renders, reports `position: sticky`, and scrolls
 * away anyway. `usePageWantsSticky` swaps in `overflow-x: clip`, which clips
 * identically without creating the scroll container.
 */
function SectionBadge({ badge }) {
  if (!badge) return null;
  if (badge.dot) {
    return <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT_TONE[badge.tone]}`} />;
  }
  return (
    <span className={`shrink-0 text-[10px] leading-none px-1.5 py-0.5 rounded-full border ${BADGE_TONE[badge.tone]}`}>
      {badge.count}
    </span>
  );
}

function TrackerTabs({ data, tab, setTab, children, onBack, subtitle }) {
  const rows = TABS.map((t) => ({ ...t, badge: t.badge ? t.badge(data) : null }));
  const current = rows.find((t) => t.id === tab) || rows[0];

  return (
    <>
      <div className="sticky top-14 z-20 -mx-4 px-4 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center gap-3 pt-2.5">
          <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full shrink-0">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0 flex items-baseline gap-2">
            <span className="text-base font-light shrink-0">Client Tracker</span>
            <span className="text-sm text-muted-foreground truncate">{subtitle}</span>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto pt-2" style={{ scrollbarWidth: "none" }}>
          {rows.map((t) => {
            const on = t.id === tab;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`shrink-0 flex items-center gap-2 text-sm px-3.5 py-2 border-b-2 transition-colors ${
                  on
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}>
                {t.label}
                <SectionBadge badge={t.badge} />
              </button>
            );
          })}
        </div>
      </div>

      {/* One line saying what this tab is for. Cheap, and it stops the tabs
          from being three words a new PM has to guess at. */}
      <p className="text-sm text-muted-foreground mt-5">{current.blurb}</p>

      <div className="space-y-6 mt-4">{children}</div>
    </>
  );
}

/* ── Meetings: what the client's Meetings page shows ───────────────────────── */

function MeetingsCard({ id, data, hydrate }) {
  const [busy, setBusy] = useState(null);
  const [savingSummaries, setSavingSummaries] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [mTitle, setMTitle] = useState("");
  const [mAt, setMAt] = useState("");
  const [mDuration, setMDuration] = useState("");
  const [mUrl, setMUrl] = useState("");
  const [mNote, setMNote] = useState("");
  const [adding, setAdding] = useState(false);

  const meetings = data.meetings_admin || [];
  const upcoming = data.upcoming_meetings || [];

  const toggleHidden = async (m) => {
    setBusy(m.id);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/meetings/${m.id}/visibility`, { hidden: !m.hidden });
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't update that meeting");
    } finally {
      setBusy(null);
    }
  };

  const toggleNotes = async (m) => {
    setBusy(m.id);
    try {
      const res = await apiClient.post(
        `/project-tracker/program/${id}/meetings/${m.id}/notes-visibility`,
        { notes_hidden: !m.notes_hidden },
      );
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't update that meeting's notes");
    } finally {
      setBusy(null);
    }
  };

  const toggleSummaries = async () => {
    setSavingSummaries(true);
    try {
      const res = await apiClient.patch(`/project-tracker/program/${id}`, {
        show_meeting_summaries: !data.show_meeting_summaries,
      });
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't save that");
    } finally {
      setSavingSummaries(false);
    }
  };

  const addMeeting = async () => {
    if (!mTitle.trim() || !mAt) return;
    setAdding(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/scheduled-meetings`, {
        title: mTitle.trim(),
        at: new Date(mAt).toISOString(),
        duration_minutes: mDuration === "" ? null : Number(mDuration),
        join_url: mUrl.trim() || null,
        note: mNote.trim() || null,
      });
      hydrate(res.data);
      setMTitle(""); setMAt(""); setMDuration(""); setMUrl(""); setMNote("");
      setAddOpen(false);
      toast.success("Meeting added to the client's dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add that meeting");
    } finally {
      setAdding(false);
    }
  };

  const removeMeeting = async (m) => {
    setBusy(m.id);
    try {
      const res = await apiClient.delete(`/project-tracker/program/${id}/scheduled-meetings/${m.id}`);
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't remove that meeting");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Video className="w-4 h-4" /> Meetings on the client dashboard</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        {/* Upcoming, PM-entered */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm">Upcoming meetings</Label>
            <Button variant="ghost" size="sm" onClick={() => setAddOpen(!addOpen)}>
              <CalendarPlus className="w-4 h-4 mr-1" /> {addOpen ? "Cancel" : "Add meeting"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Fathom only knows about calls after they happen — anything you've booked with the client goes here,
            and shows in the "Next meeting" card on their dashboard.
          </p>
          {addOpen && (
            <div className="rounded-xl border p-4 space-y-2 mb-3">
              <div className="grid sm:grid-cols-2 gap-2">
                <Input value={mTitle} onChange={(e) => setMTitle(e.target.value)} placeholder="Meeting title (e.g. V1 review call)" />
                <Input type="datetime-local" value={mAt} onChange={(e) => setMAt(e.target.value)} />
                <Input type="number" min="0" value={mDuration} onChange={(e) => setMDuration(e.target.value)} placeholder="Duration (minutes, optional)" />
                <Input value={mUrl} onChange={(e) => setMUrl(e.target.value)} placeholder="Join link (optional)" />
              </div>
              <Input value={mNote} onChange={(e) => setMNote(e.target.value)} placeholder="Short note for the client (optional)" />
              <Button size="sm" className="rounded-full" onClick={addMeeting} disabled={adding || !mTitle.trim() || !mAt}>
                {adding && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Add to dashboard
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {upcoming.length === 0 && !addOpen && <p className="text-xs text-muted-foreground">Nothing booked.</p>}
            {upcoming.map((m) => (
              <div key={m.id} className="rounded-xl border p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.date_text} · {m.time_text}{m.duration_text ? ` · ${m.duration_text}` : ""}{m.join_url ? " · has join link" : ""}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeMeeting(m)} disabled={busy === m.id} className="shrink-0">
                  {busy === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-muted-foreground" />}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Recorded (Fathom) */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <Label className="text-sm">Recorded meetings (Fathom)</Label>
            <button onClick={toggleSummaries} disabled={savingSummaries}
              className="flex items-center gap-2 text-xs text-muted-foreground">
              {savingSummaries ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                <span className={`w-8 h-4.5 rounded-full relative transition-colors ${data.show_meeting_summaries ? "bg-green-600" : "bg-muted-foreground/30"}`}
                  style={{ height: 18 }}>
                  <span className="absolute top-0.5 w-3.5 h-3.5 rounded-full bg-[#FFFFFF] transition-all"
                    style={{ left: data.show_meeting_summaries ? 16 : 2 }} />
                </span>
              )}
              Share meeting notes with the client
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Calls linked to this program appear on the client's Meetings page automatically, with the recording
            and the notes. Two ways to hold something back: <strong className="text-foreground">Hide</strong> takes
            the whole call off their page, and <strong className="text-foreground">Notes off</strong> leaves the
            call visible but withholds the write-up — that's the one for a sales conversation the client sat on.
          </p>
          <div className="space-y-2">
            {(data.meetings_admin || []).length === 0 && (
              <p className="text-xs text-muted-foreground">No Fathom calls are linked to this program yet.</p>
            )}
            {meetings.map((m) => (
              <div key={m.id} className={`rounded-xl border p-3 flex items-center gap-3 ${m.hidden ? "opacity-55" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {m.date_text}{m.time_text ? ` · ${m.time_text}` : ""}{m.duration_text ? ` · ${m.duration_text}` : ""}
                    {m.recording_url ? " · recording" : " · no recording link"}
                  </p>
                  {/* What the client can see on this row, said outright — it's
                      the product of two switches plus the project default, and
                      making a PM combine them in their head is how a sales call
                      ends up shared. */}
                  <p className="text-xs mt-0.5">
                    {m.hidden
                      ? <span className="text-muted-foreground">Hidden from the client</span>
                      : m.client_sees_notes
                        ? <span className="text-green-700">Client sees the call and the notes</span>
                        : <span className="text-amber-700">Client sees the call, not the notes</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!m.hidden && (
                    <Button variant="outline" size="sm" className="rounded-full text-xs h-7"
                      onClick={() => toggleNotes(m)} disabled={busy === m.id}>
                      {m.notes_hidden ? "Notes on" : "Notes off"}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="rounded-full text-xs h-7"
                    onClick={() => toggleHidden(m)} disabled={busy === m.id}>
                    {busy === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : m.hidden ? <><Eye className="w-3.5 h-3.5 mr-1" /> Show</>
                      : <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Documents: library resources shared to the client dashboard ───────────── */

function DocumentsCard({ id, data, hydrate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);

  const attachedIds = new Set((data.documents || []).map((d) => d.id));

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiClient.get("/resources/", { params: { search: q, limit: 8 } });
        setResults(res.data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const attach = async (r) => {
    setBusy(r.id);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/documents`, { resource_id: r.id });
      hydrate(res.data);
      toast.success(`"${r.title}" is now on the client's dashboard`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't attach that document");
    } finally {
      setBusy(null);
    }
  };

  const syncDefaults = async () => {
    setBusy("defaults");
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/documents/sync-defaults`);
      hydrate(res.data);
      toast.success(res.data.added
        ? `Added ${res.data.added} default document${res.data.added === 1 ? "" : "s"}.`
        : "Already has every default document.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't sync the defaults");
    } finally {
      setBusy(null);
    }
  };

  const detach = async (d) => {
    setBusy(d.id);
    try {
      const res = await apiClient.delete(`/project-tracker/program/${id}/documents/${d.id}`);
      hydrate(res.data);
    } catch (e) {
      toast.error("Couldn't remove that document");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Documents on the client dashboard</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Share anything from the Resources library — proposals, schedules, guides. PDFs open right on the
            client's Documents page; the client never needs an HQ login. Anything flagged
            "share by default" in Resources — the Client Welcome Guide, for one — is attached to new
            projects automatically.
          </p>
          <Button variant="outline" size="sm" className="rounded-full text-xs h-8 shrink-0"
            onClick={syncDefaults} disabled={busy === "defaults"}>
            {busy === "defaults" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Add defaults
          </Button>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the Resources library…" className="pl-9" />
        </div>
        {searching && <p className="text-xs text-muted-foreground">Searching…</p>}
        {results.length > 0 && (
          <div className="rounded-xl border divide-y">
            {results.map((r) => (
              <div key={r.id} className="p-3 flex items-center gap-3">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{r.title}</p>
                  <p className="text-xs text-muted-foreground">{[r.type?.toUpperCase(), r.category].filter(Boolean).join(" · ")}</p>
                </div>
                {attachedIds.has(r.id) ? (
                  <span className="text-xs text-green-700 flex items-center gap-1 shrink-0"><Check className="w-3.5 h-3.5" /> Shared</span>
                ) : (
                  <Button variant="outline" size="sm" className="rounded-full text-xs h-7 shrink-0"
                    onClick={() => attach(r)} disabled={busy === r.id}>
                    {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Share</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          {(data.documents || []).length === 0 && <p className="text-xs text-muted-foreground">Nothing shared yet.</p>}
          {(data.documents || []).map((d) => (
            <div key={d.id} className="rounded-xl border p-3 flex items-center gap-3">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{d.title}</p>
                <p className="text-xs text-muted-foreground">
                  {[d.type?.toUpperCase(), d.category, d.size_text, d.added_text ? `shared ${d.added_text}` : null].filter(Boolean).join(" · ")}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => detach(d)} disabled={busy === d.id} className="shrink-0">
                {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-muted-foreground" />}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Delivery forecasts: the PM's two-or-three-weeks-out call ──────────────── */
/*
 * The workflow this replaces: the studio knows a fortnight ahead when a
 * milestone will land, the PM tells the client if they remember, the payment
 * falls due, the client goes quiet, and then the PM, the accountant and sales
 * all chase the same person. Recorded here once, the client's own page does the
 * telling — before the invoice rather than after it.
 *
 * Open to every PM, including the ones who can't see dollar figures. Setting a
 * date is a production call, not a finance one, and gating it on money
 * visibility would put it out of reach of exactly the people who know the date.
 */

function ForecastCard({ id, data, hydrate }) {
  const payments = data.payments || {};
  const milestones = payments.milestones || [];
  const [editing, setEditing] = useState(null);   // milestone id
  const [form, setForm] = useState({ start: "", end: "", confidence: "estimated", note: "" });
  const [busy, setBusy] = useState(false);

  if (!payments.has_schedule) return null;

  const open = (m) => {
    setEditing(m.id);
    setForm({
      start: m.forecast?.start || "",
      end: m.forecast?.end || "",
      confidence: m.forecast?.confidence || "estimated",
      note: m.forecast?.note || "",
    });
  };

  const save = async (milestoneId, payload) => {
    setBusy(true);
    try {
      await apiClient.put(
        `/project-tracker/program/${id}/milestones/${milestoneId}/forecast`, payload,
      );
      const res = await apiClient.get(`/project-tracker/program/${id}`);
      hydrate(res.data);
      setEditing(null);
      toast.success(payload.clear ? "Forecast removed" : "The client can now see this window");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that forecast");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarPlus className="w-4 h-4" /> Delivery forecasts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          When you know a milestone's window — usually two or three weeks out — put it here. The client
          sees it on their dashboard alongside the payment it triggers, so the invoice arrives as a
          reminder rather than a surprise. A window is fine; it doesn't have to be a single day.
        </p>

        <div className="rounded-xl border divide-y">
          {milestones.map((m) => {
            const f = m.forecast;
            const isEditing = editing === m.id;
            return (
              <div key={m.id} className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{m.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {f ? (
                        <span className={f.confidence === "confirmed" ? "text-green-700" : ""}>
                          {f.confidence_text} · {f.window_text}
                          {f.note ? ` · "${f.note}"` : ""}
                        </span>
                      ) : m.state === "paid" || m.state === "waived" ? (
                        /* A settled milestone needs no forecast — flagging it
                           would train PMs to ignore the flag on the ones that do. */
                        <span>Settled — nothing to forecast</span>
                      ) : (
                        <span className="text-amber-700">No forecast yet — the client sees no date for this milestone</span>
                      )}
                    </p>
                  </div>
                  {m.state === "paid" ? (
                    <span className="text-xs text-muted-foreground shrink-0">Settled</span>
                  ) : (
                    <Button variant="ghost" size="sm" className="shrink-0"
                      onClick={() => (isEditing ? setEditing(null) : open(m))}>
                      {isEditing ? "Cancel" : f ? "Edit" : "Set window"}
                    </Button>
                  )}
                </div>

                {isEditing && (
                  <div className="mt-3 space-y-3 rounded-lg bg-muted/40 p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Window opens</Label>
                        <Input type="date" value={form.start}
                          onChange={(e) => setForm({ ...form, start: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Window closes (optional)</Label>
                        <Input type="date" value={form.end}
                          onChange={(e) => setForm({ ...form, end: e.target.value })} />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">How sure are you?</Label>
                      <div className="flex gap-2 mt-1">
                        {[
                          ["estimated", "Projected", "Client reads it as our current projection"],
                          ["confirmed", "Confirmed", "Client reads it as a commitment from the team"],
                        ].map(([value, label, hint]) => (
                          <button key={value} type="button" title={hint}
                            onClick={() => setForm({ ...form, confidence: value })}
                            className={`text-xs px-3 py-1.5 rounded-full border ${
                              form.confidence === value
                                ? "bg-foreground text-background border-foreground"
                                : "text-muted-foreground"
                            }`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs">A line for the client (optional)</Label>
                      <Input value={form.note} maxLength={140}
                        placeholder="e.g. Pending the final landscaping package from your architect"
                        onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>

                    <div className="flex items-center gap-2">
                      <Button size="sm" disabled={busy || !form.start}
                        onClick={() => save(m.id, form)}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                        Save and show the client
                      </Button>
                      {f && (
                        <Button size="sm" variant="ghost" disabled={busy}
                          onClick={() => save(m.id, { clear: true })}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Final payment heads-up: three weeks' notice, approved by the PM ───────── */
/*
 * Bernard, 2026-11-02: the client gets a three-week heads-up on the final
 * payment, but only once the PM approves it — "things change all the time".
 * HQ asks four weeks before the finals (Today list, bell, email); this card is
 * where the PM answers. The due date is the finals date, pushed out so the
 * client always has 21 days. No dollar figures: the client's email carries the
 * amount, the PM's page never does. See services/final_payment_notice.py.
 */

const NOTICE_DAYS = 21;

function fmtDay(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The due date an approval today would set — mirrors due_for() on the server. */
function dueFor(finals) {
  const earliest = new Date();
  earliest.setDate(earliest.getDate() + NOTICE_DAYS);
  const floor = isoDay(earliest);
  return finals && finals > floor ? finals : floor;
}

function FinalPaymentNoticeCard({ id, data, hydrate, readOnly }) {
  const notice = data.final_payment_notice;
  const [open, setOpen] = useState(false);
  const [finals, setFinals] = useState("");
  const [busy, setBusy] = useState(false);

  if (!notice || notice.status === "settled") return null;

  const mode = (data.email_modes || {}).final_payment;
  const start = () => {
    setFinals(notice.expected_date || isoDay(new Date()));
    setOpen(true);
  };

  const act = async (payload) => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/project-tracker/program/${id}/final-payment-notice`, payload);
      const fresh = await apiClient.get(`/project-tracker/program/${id}`);
      hydrate(fresh.data);
      setOpen(false);
      if (payload.action === "snooze") {
        toast.success("OK — HQ will ask you again in 3 days");
      } else if (res.data.sent) {
        toast.success(`Heads-up sent — the final payment is due ${res.data.due_text}`);
      } else {
        toast.warning(
          `Approved — due ${res.data.due_text}. The email did not go out (${res.data.send_status}`
          + `${res.data.error ? `: ${res.data.error}` : ""}).`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setBusy(false);
    }
  };

  const line = {
    no_date: (
      <span className="text-amber-700">
        No finals date yet. Set a delivery forecast on the final payment above, and HQ will ask you
        to approve the heads-up four weeks before it.
      </span>
    ),
    waiting: (
      <span>
        Finals expected {fmtDay(notice.expected_date)}. HQ will ask you to approve the heads-up
        from {fmtDay(notice.ask_from)}.
      </span>
    ),
    needs_approval: (
      <span className="text-amber-700 font-medium">
        Needs your approval{notice.expected_date ? ` — finals expected ${fmtDay(notice.expected_date)}` : ""}.
        Nothing goes to the client until you approve it.
      </span>
    ),
    snoozed: <span>You said not yet. HQ asks again on {fmtDay(notice.snooze_until)}.</span>,
    confirmed: (
      <span className="text-green-700">
        Approved{notice.confirmed_by_name ? ` by ${notice.confirmed_by_name}` : ""} on {fmtDay(notice.confirmed_at)}
        {" "}· due {fmtDay(notice.confirmed_due)}
        {notice.sent_status && !["sent", "redirected"].includes(notice.sent_status)
          ? ` · the email did not go out (${notice.sent_status})` : ""}
      </span>
    ),
  }[notice.status];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BellRing className="w-4 h-4" /> Final payment heads-up
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          The client gets at least three weeks' notice of the final payment so their team can process
          it. It only goes out when you approve the finals date here.
        </p>
        <div className="rounded-xl border p-3 flex flex-wrap items-start gap-3">
          <p className="text-sm flex-1 min-w-[12rem]">{line}</p>
          {!readOnly && !open && notice.status !== "no_date" && (
            <Button size="sm" variant={notice.status === "needs_approval" ? "default" : "outline"} onClick={start}>
              {notice.status === "confirmed" ? "Send a corrected notice" : "Review & approve"}
            </Button>
          )}
        </div>

        {open && (
          <div className="space-y-3 rounded-lg bg-muted/40 p-3">
            <div className="max-w-xs">
              <Label className="text-xs">Finals ready on</Label>
              <Input type="date" value={finals} min={isoDay(new Date())}
                onChange={(e) => setFinals(e.target.value)} />
            </div>
            <p className="text-sm">
              The client will be told the final payment is due <strong>{fmtDay(dueFor(finals))}</strong>
              {finals && dueFor(finals) !== finals ? " — moved out so they get the full three weeks" : ""}.
              It goes to their Accounts Payable contact, with the primary contact copied.
            </p>
            {mode && mode !== "live" && (
              <p className="text-xs text-amber-700 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Final payment emails are {mode} in Tracker Admin, so the client won't actually be emailed.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" disabled={busy || !finals}
                onClick={() => act({ action: "approve", finals_date: finals })}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : null}
                Approve & send to client
              </Button>
              {notice.status !== "confirmed" && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => act({ action: "snooze" })}>
                  Not yet — ask me in 3 days
                </Button>
              )}
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── The VEXOS rail, mirrored ───────────────────────────────────────────────
 *
 * The client sees two delivery rails side by side — visualization and VEXOS —
 * because they run in parallel and are different productions. This page only
 * ever showed the first, so "did the client get V1?" took two pages to answer.
 *
 * Read-only on purpose: `program.vexos_tracker.stage` is the source of truth
 * and the VEXOS manager drives it from the project's own VEXOS tab. Mirroring
 * the control here would give two places to change one value.
 */
function VexosRailCard({ data, programId }) {
  const rounds = data.vexos_rounds || [];
  if (!rounds.length) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Rocket className="w-4 h-4" /> VEXOS platform rail
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          What the client sees on their second rail. Driven from this project's{" "}
          <Link to={`/programs/${programId}?tab=vexos`} className="underline font-medium">VEXOS tab</Link>{" "}
          — shown here so you don't have to go and look.
        </p>
        <div className="rounded-xl border divide-y">
          {rounds.map((r) => (
            <div key={r.key} className="p-3 flex items-center gap-3">
              <span className="font-mono text-xs text-muted-foreground shrink-0 w-16">{r.label}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{r.title}</p>
                <p className="text-xs text-muted-foreground">
                  Client sees: {r.status_text}
                  {r.delivered_text ? ` · sent ${r.delivered_text}` : ""}
                </p>
              </div>
              <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full ${
                r.status === "approved" ? "bg-green-100 text-green-700"
                  : r.status === "delivered" ? "bg-blue-100 text-blue-700"
                  : "bg-muted text-muted-foreground"
              }`}>
                {r.status === "approved" ? "Approved" : r.status === "delivered" ? "With client" : "Not sent"}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── What actually left the building ────────────────────────────────────────
 *
 * The backend has always built and returned this log. Nothing rendered it, so a
 * PM pressing Send got a toast and then no way to check afterwards — which
 * matters most in exactly the case the toast is least trustworthy: the email
 * module ships PAUSED, and in that state a send is recorded, the round is
 * marked delivered, and nothing is emailed.
 */
function SendHistoryCard({ data }) {
  const log = data.delivery_log || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="w-4 h-4" /> What we've sent
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.email_mode !== "live" && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              Delivery emails are <strong>{data.email_mode || "paused"}</strong>, so a send
              records the delivery and the date but doesn't reach the client. Each kind of email
              is switched separately — change this one in{" "}
              <Link to="/admin/tracker" className="underline font-medium">Tracker Admin</Link>.
            </p>
          </div>
        )}
        {log.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing sent from this tracker yet.</p>
        ) : (
          <div className="rounded-xl border divide-y">
            {log.map((e, i) => {
              // Delivery sends are logged as sent_at / sent_by_name; read both
              // shapes so no row loses its time or sender.
              const at = e.at || e.sent_at;
              const by = e.by_name || e.sent_by_name || e.by;
              return (
              <div key={`${at || i}-${i}`} className="p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    {e.round_label || e.kind || "Delivery"}
                    {e.round_title ? ` · ${e.round_title}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[
                      e.at_text || (at ? new Date(at).toLocaleString() : null),
                      by,
                      e.sent != null ? `${e.sent} recipient${e.sent === 1 ? "" : "s"}` : null,
                    ].filter(Boolean).join(" · ")}
                  </p>
                  {/* Who it was addressed to, so "did the architect get the
                      final?" is answerable here — typed and CC'd people too. */}
                  {(e.recipients?.length > 0 || e.cc?.length > 0) && (
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">
                      {e.recipients?.length > 0 && `To ${e.recipients.join(", ")}`}
                      {e.cc?.length > 0 && `${e.recipients?.length ? " · " : ""}CC ${e.cc.join(", ")}`}
                    </p>
                  )}
                  {e.reason && <p className="text-xs text-amber-700 mt-0.5">{e.reason}</p>}
                </div>
                <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full ${
                  e.sent ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"
                }`}>
                  {e.sent ? "Emailed" : "Recorded only"}
                </span>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Team: people the client added from their side ─────────────────────────── */

/* The three people the client sees on their portal, and the one of them HQ has
 * to be told about.
 *
 * The project lead comes from the program's assignment and the sales rep from
 * intake attribution — both already have an owner elsewhere in HQ, and a second
 * place to set them is a second place for them to disagree. The VEXOS manager
 * has no such home: it is stamped the first time somebody sends a platform
 * delivery or the go-live sign-off, which covers the normal case and leaves
 * this for the one where it needs correcting.
 */
function ContactCardsCard({ id }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/project-tracker/program/${id}/contacts`);
      setState(res.data);
    } catch (e) {
      setState({ contacts: [], candidates: [], has_vexos: false });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setManager = async (userId) => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/project-tracker/program/${id}/vexos-manager`, {
        user_id: userId || null,
      });
      setState((s) => ({ ...s, ...res.data }));
      toast.success(userId ? "Saved — the client's portal names them now" : "Cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save that");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;
  const contacts = state.contacts || [];

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Who the client can contact</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          These are the contact cards on the client's dashboard — name, role, email and photo.
          The photo is whatever that person uploaded on their own Settings page.
        </p>
        {contacts.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Nobody is assigned to this project yet, so the client sees no contact cards at all.
          </p>
        )}
        <div className="space-y-2">
          {contacts.map((c) => (
            <div key={c.key} className="rounded-xl border p-3 flex items-center gap-3">
              {c.photo_url
                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt=""
                    className="w-9 h-9 rounded-full object-cover shrink-0" />
                : <span className="w-9 h-9 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center shrink-0">
                    {c.initials || "?"}
                  </span>}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[c.role, c.email].filter(Boolean).join(" · ")}
                </p>
              </div>
              {!c.photo_url && (
                <span className="text-xs text-muted-foreground shrink-0">no photo</span>
              )}
            </div>
          ))}
        </div>

        {state.has_vexos && (
          <div className="pt-1">
            <Label className="text-sm">VEXOS manager</Label>
            <select
              className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={state.vexos_manager_id || ""}
              disabled={saving}
              onChange={(e) => setManager(e.target.value)}
            >
              <option value="">Nobody — hide this card</option>
              {(state.candidates || []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Set automatically the first time someone sends this client a platform delivery or the
              go-live sign-off. Change it here if that isn't who they should be writing to.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TeamCard({ data, onChanged }) {
  const [busy, setBusy] = useState(null);
  const members = data.team_members || [];

  // Removal reuses the client-side endpoint — the PM panel already knows the
  // tracker's token, and access control on the client side IS the token.
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the project dashboard?`)) return;
    setBusy(m.id);
    try {
      await apiClient.delete(`/project-tracker/client/${data.token}/team/${m.id}`);
      await onChanged();
    } catch (e) {
      toast.error("Couldn't remove them");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Client-side team</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          People the client added to their dashboard themselves (each was emailed the link). Nothing for you to
          manage — this is just visibility into who's watching the project.
        </p>
        {members.length === 0 && <p className="text-xs text-muted-foreground">The client hasn't added anyone yet.</p>}
        <div className="space-y-2">
          {members.map((m) => (
            <div key={m.id} className="rounded-xl border p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[m.email, m.title, m.added_text ? `added ${m.added_text}` : null].filter(Boolean).join(" · ")}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(m)} disabled={busy === m.id} className="shrink-0">
                {busy === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-muted-foreground" />}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
